// Camera scan-as-modal overlay for the POS. Ported from waio-inventory's
// ScanModal (camera viewfinder + manual-entry fallback) and adapted:
//
//   - Uses `useCameraScanner` (the renamed STRICH/ZXing/MLKit hook).
//   - On every decode it calls `onScan(code)` — the POS's EXISTING
//     handleScan (barcode -> /products/by-barcode -> handleAddToCart, with its
//     own availability gate + toasts). Zero new add logic; identical to the
//     keyboard-wedge path.
//   - CONTINUOUS: after each add it re-arms the camera so the cashier can scan
//     item after item without re-tapping. A short de-dupe window guards against
//     the same in-frame barcode re-firing across the one-shot hook's re-init.
//
// Additive only — does not touch the keyboard-wedge scanner.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useCameraScanner } from '@/hooks/useCameraScanner'
import { X, ScanLine, Keyboard, Camera, Loader2 } from 'lucide-react'

interface ScanModalProps {
  /** The POS scan handler (barcode -> product -> add to cart). Reused verbatim. */
  onScan: (code: string) => void | Promise<void>
  onClose: () => void
}

export function ScanModal({ onScan, onClose }: ScanModalProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { platform, supported, scanning, scan, ready, stop } = useCameraScanner({
    containerRef,
  })

  const [manualInput, setManualInput] = useState('')
  const [busy, setBusy] = useState(false)
  const openRef = useRef(true)
  const loopStartedRef = useRef(false)
  // De-dupe across the one-shot hook's per-scan camera re-init: the same
  // barcode lingering in frame could re-fire on the fresh reader.
  const lastRef = useRef<{ code: string; t: number } | null>(null)
  // Always invoke the LATEST onScan. handleScan closes over live cart /
  // customer state, so the continuous loop (which captures handleCode once)
  // must reach the current one via a ref — mirrors the keyboard-wedge path.
  const onScanRef = useRef(onScan)
  useEffect(() => {
    onScanRef.current = onScan
  })

  useEffect(() => {
    openRef.current = true
    return () => {
      openRef.current = false
    }
  }, [])

  const handleCode = useCallback(async (raw: string) => {
    const code = raw.trim()
    if (!code) return
    const now = Date.now()
    if (
      lastRef.current &&
      lastRef.current.code === code &&
      now - lastRef.current.t < 2500
    ) {
      return
    }
    lastRef.current = { code, t: now }
    setBusy(true)
    try {
      await onScanRef.current(code) // existing barcode -> add-to-cart (+ toasts)
    } finally {
      setBusy(false)
    }
  }, [])

  // Continuous loop: scan -> add -> re-arm, until the modal closes (stop()
  // resolves scan() with null). The short pause lets the cashier swap items
  // and keeps the camera from re-reading the same item the instant it re-arms.
  const runLoop = useCallback(async () => {
    while (openRef.current) {
      const code = await scan()
      if (code === null || !openRef.current) return
      await handleCode(code)
      await new Promise((r) => setTimeout(r, 500))
    }
  }, [scan, handleCode])

  useEffect(() => {
    if (!ready || !supported || loopStartedRef.current) return
    loopStartedRef.current = true
    void runLoop()
  }, [ready, supported, runLoop])

  const handleManualSubmit = () => {
    const v = manualInput.trim()
    if (!v) return
    setManualInput('')
    void handleCode(v)
  }

  const handleClose = () => {
    openRef.current = false
    stop()
    onClose()
  }

  const statusLine = !ready
    ? 'Checking camera availability…'
    : busy
      ? 'Adding to cart…'
      : platform === 'web'
        ? scanning
          ? 'Hold a barcode in frame to add it'
          : 'Starting camera…'
        : platform === 'native'
          ? scanning
            ? 'Scanner is open'
            : 'Opening camera…'
          : 'Camera scanning is unavailable here — use search below'

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/70 backdrop-blur-sm animate-fade-in"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      onClick={handleClose}
    >
      <header className="px-3 py-3 flex-shrink-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-2">
          <h1 className="font-semibold text-white">Scan to add</h1>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/10 cursor-pointer text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main
        className="flex-1 flex flex-col items-center justify-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full max-w-md aspect-square bg-gray-900 rounded-2xl overflow-hidden flex items-center justify-center shadow-2xl">
          {platform === 'web' && (
            <div ref={containerRef} className="relative w-full h-full" />
          )}

          {platform !== 'web' && (
            <>
              {scanning ? (
                <Loader2 className="w-12 h-12 text-green-400 animate-spin" />
              ) : (
                <Camera className="w-16 h-16 text-gray-600" />
              )}
              <ScanLine className="absolute left-6 right-6 top-1/2 -translate-y-1/2 w-auto h-1 text-green-500" />
            </>
          )}

          <p className="absolute top-4 left-0 right-0 text-center text-sm text-white drop-shadow z-10 pointer-events-none">
            Scan a product to add it to the cart
          </p>
          <p className="absolute bottom-4 left-0 right-0 text-center text-xs text-white/80 drop-shadow px-4 z-10 pointer-events-none">
            {statusLine}
          </p>
          {busy && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-20 pointer-events-none">
              <Loader2 className="w-10 h-10 text-green-400 animate-spin" />
            </div>
          )}
        </div>

        <div className="w-full max-w-md mt-6">
          <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
            <Keyboard className="w-4 h-4" />
            Search / enter barcode
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleManualSubmit()
              }}
              placeholder="Enter Name / SKU / Barcode"
              className="flex-1 h-11 px-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={handleManualSubmit}
              disabled={!manualInput.trim() || busy}
              className="h-11 px-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-xl cursor-pointer transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      </main>

      <footer className="p-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-2xl mx-auto">
          <button
            type="button"
            onClick={handleClose}
            className="w-full h-12 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-xl cursor-pointer transition-colors"
          >
            Done
          </button>
        </div>
      </footer>
    </div>
  )
}
